import { getGreeting } from '../../lib/greeting'
import { getWelcomeLine, type PantryState } from '../../lib/welcomeLine'

type Props = {
  firstName:  string | null
  state:      PantryState
  // Select mode — when on, the masthead becomes a sticky toolbar.
  selectMode?:     boolean
  onEnterSelect?:  () => void
  onCancelSelect?: () => void
  onSelectAll?:    () => void
  onClearSelect?:  () => void
  selectedCount?:  number
  allSelected?:    boolean
}

export function HomeMasthead({
  firstName, state,
  selectMode = false,
  onEnterSelect,
  onCancelSelect,
  onSelectAll,
  onClearSelect,
  selectedCount = 0,
  allSelected   = false,
}: Props) {
  // Select-mode toolbar takes over the masthead so the count/cancel/all
  // controls stay reachable while the user scrolls a long list.
  if (selectMode) {
    return (
      <header className="sticky top-0 z-20 bg-cream border-b border-border-sand px-4 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onCancelSelect}
            className="text-[13px] font-semibold text-ink-muted py-1"
          >
            Cancel
          </button>

          <div className="font-serif text-[15px] font-medium text-ink">
            {selectedCount === 0 ? 'Select items' : `${selectedCount} selected`}
          </div>

          <button
            type="button"
            onClick={allSelected ? onClearSelect : onSelectAll}
            className="text-[13px] font-semibold text-forest py-1"
          >
            {allSelected ? 'Clear' : 'All'}
          </button>
        </div>
      </header>
    )
  }

  const { salutation, italicWord } = getGreeting()
  const welcome   = getWelcomeLine(state)
  const dateLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'short',
    month:   'short',
    day:     'numeric',
  }).toUpperCase()
  const initial = (firstName?.[0] ?? 'M').toUpperCase()
  const name    = firstName || 'there'

  return (
    <header className="px-4 pt-8 pb-5 mb-4 border-b border-border-sand">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] tracking-[0.18em] text-ink-muted font-medium">
          {dateLabel}
        </div>
        <div className="flex items-center gap-2">
          {onEnterSelect && (
            <button
              type="button"
              onClick={onEnterSelect}
              className="rounded-[10px] border border-border-sand bg-paper px-3 py-1 text-[12px] font-semibold text-ink-muted"
            >
              Edit
            </button>
          )}
          <div
            className="w-[30px] h-[30px] rounded-full flex items-center justify-center font-serif text-[13px] font-medium text-ink"
            style={{ border: '1px solid var(--border-avatar)' }}
          >
            {initial}
          </div>
        </div>
      </div>

      <h1 className="font-serif text-[34px] leading-[1.05] tracking-[-0.015em] text-ink font-normal">
        {italicWord ? (
          <>
            {salutation} <em className="italic">{italicWord},</em>
            <br />
            {name}.
          </>
        ) : (
          <>{salutation}, {name}.</>
        )}
      </h1>

      <p className="font-serif text-[14px] italic text-ink-muted mt-[10px] leading-[1.4]">
        {welcome}
      </p>
    </header>
  )
}
